import {
  TableSortLabel,
  Paper,
  Alert,
  IconButton,
  Stack,
  Box,
} from "@mui/material";
import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { roundStringNumberToInt } from "../../helpers/businessleaderDashboardHelpers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { additionalQueriestableHeadings04 } from "./AdditionalQueriesConstants";
import DownloadIcon from "@mui/icons-material/Download";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

interface Props {
  mtdQftdBudgetControlIdData: any;
}

export const QFTDBudgetControl: React.FC<Props> = React.memo(
  ({ mtdQftdBudgetControlIdData }) => {
    const { selectedStartDate, selectedEndDate, selectedDaysLeftForPeriod } =
      useSelector((state: any) => state?.businessLeaderDashBoard);
    const headCells = Object.entries(additionalQueriestableHeadings04);

    const rows = mtdQftdBudgetControlIdData;

    const [order, setOrder] = useState("desc") as any;
    const [orderBy, setOrderBy] = useState("qftd_budget") as any;

    const daysLeftForPeriod = selectedDaysLeftForPeriod;

    let endDate = selectedEndDate;

    const twoDaysAgo = dayjs().subtract(2, "day").format("YYYY-MM-DD");
    if (selectedEndDate > twoDaysAgo) {
      endDate = twoDaysAgo;
    }

    let message = "";

    if (selectedStartDate > twoDaysAgo) {
      message = `No Data Available, Data is only relevant for current month`;
    } else {
      message = `Data is only relevant for the period of ${dayjs(
        selectedStartDate
      ).format("DD-MM-YYYY")} - ${dayjs(endDate).format(
        "DD-MM-YYYY"
      )}, Days left for the selected period: ${daysLeftForPeriod}`;
    }

    const parseValue = (value: any) => {
      if (typeof value === "string") {
        if (value.includes("%")) {
          return parseFloat(value.replace("%", ""));
        }
        if (value.includes("$")) {
          return parseFloat(value.replace("$", ""));
        } else {
          return value.toLowerCase();
        }
      } else if (value === null || !value) {
        return "";
      }
      return value;
    };

    const handleRequestSort = (property: any) => {
      const isAscending = orderBy === property && order === "asc";
      setOrder(isAscending ? "desc" : "asc");
      setOrderBy(property);
    };

    const sortedRows = [...rows].sort((a, b) => {
      const aValue = parseValue(a[orderBy]);
      const bValue = parseValue(b[orderBy]);

      if (aValue < bValue) {
        return order === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    });

    const handleExportCSV = () => {
      const csvRows = [];

      // Extract the table headers
      const headers = headCells.map((pair) => pair[0]) as any;
      csvRows.push(headers.join(","));

      // Extract the table data
      // eslint-disable-next-line
      sortedRows.map((row: any) => {
        let rowData = [] as any;
        rowData.push(row?.brand_id);
        rowData.push(row?.brand);
        rowData.push(Math.round(row?.qftd));
        rowData.push(Math.round(row?.qftd_budget));
        rowData.push(Math.round(row?.perc_Qftd_budget_use * 100) + "%");
        rowData.push(Math.round(row?.Qftd_RR));
        rowData.push(Math.round(row?.perc_of_RR_qftd_budget_use * 100) + "%");
        csvRows.push(rowData.join(","));
      });

      // Convert CSV rows to a single string
      const csvString = csvRows.join("\n");

      // Create a blob and download the file
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "QFTDBudgetControl.csv");
    };

    const getStyleHightLight = (value: any) => {
      const updatedval = roundStringNumberToInt(value * 100) as any;
      if (updatedval >= 95) {
        return {
          border: "2px solid red",
          borderRadius: "4px",
          padding: "8px",
          backgroundColor: "#ffcccc",
          width: "fit-content",
        }; //light red
      } else if (updatedval >= 85) {
        return {
          border: "2px solid #ffa3a3",
          borderRadius: "4px",
          padding: "8px",
          backgroundColor: "#fff0f0",
          width: "fit-content",
        }; //red
      }
    };

    return (
      <>
        <TableContainer
          className="RunRateTableWrap"
          component={Paper}
          sx={{ boxShadow: "none", maxHeight: 400, overflowY: "auto" }}
        >
          {sortedRows?.length > 0 ? (
            <>
              <Stack flexDirection={"row"} alignItems="center" gap={2}>
                <Alert
                  sx={{
                    color: "#ED6C02",
                    margin: "16px",
                    border: "1px solid #ED6C0280",
                    width: "100%",
                  }}
                  severity="warning"
                  icon={<InfoOutlinedIcon fontSize="inherit" sx={{ mr: 1 }} />}
                >
                  {message}
                </Alert>
                <IconButton color="secondary" size="large" data-tracker="278">
                  <DownloadIcon fontSize="medium" onClick={handleExportCSV} />
                </IconButton>
              </Stack>
              <Table aria-label="collapsible table" size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {headCells.map(([key, value], index) => (
                      <TableCell
                        key={index + "tabelcell" + Math.random()}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === key}
                          direction={orderBy === key ? order : "asc"}
                          onClick={() => handleRequestSort(key)}
                        >
                          {value}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows?.map((row: any, rowIndex: any) => (
                    <React.Fragment
                      key={rowIndex + "sortedRowsQFTD" + Math.random()}
                    >
                      <TableRow
                        sx={{
                          "&:not(:last-child) > *": {
                            borderBottom:
                              "1px solid rgba(224, 224, 224, 1) !important",
                          },
                        }}
                      >
                        <TableCell
                          key={1 + "cellQftd" + Math.random()}
                          align="left"
                        >
                          {row?.brand_id}
                        </TableCell>
                        <TableCell
                          key={2 + "cellQftd" + Math.random()}
                          align="left"
                        >
                          {row?.brand}
                        </TableCell>
                        <TableCell
                          key={3 + "cellQftd" + Math.random()}
                          align="left"
                        >
                          {roundStringNumberToInt(row?.qftd)}
                        </TableCell>
                        <TableCell
                          key={4 + "cellQftd" + Math.random()}
                          align="left"
                        >
                          {roundStringNumberToInt(row?.qftd_budget)}
                        </TableCell>
                        <TableCell
                          key={5 + "cellQftd" + Math.random()}
                          align="left"
                        >
                          <Box
                            style={getStyleHightLight(
                              row?.perc_Qftd_budget_use
                            )}
                          >
                            {roundStringNumberToInt(
                              row?.perc_Qftd_budget_use * 100
                            ) + "%"}
                          </Box>
                        </TableCell>
                        <TableCell
                          key={6 + "cellQftd" + Math.random()}
                          align="left"
                        >
                          {roundStringNumberToInt(row?.Qftd_RR)}
                        </TableCell>
                        <TableCell
                          key={7 + "cellQftd" + Math.random()}
                          align="left"
                        >
                          <Box
                            style={getStyleHightLight(
                              row?.perc_of_RR_qftd_budget_use
                            )}
                          >
                            {roundStringNumberToInt(
                              row?.perc_of_RR_qftd_budget_use * 100
                            ) + "%"}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : (
            ""
          )}
        </TableContainer>
      </>
    );
  }
);
